// Shared locale-detection logic.
// CommonJS exports so it can be `require()`d by the Node/dev-proxy
// middleware (.cjs) AND `import`ed by the ESM i18n config (Babel interop).
//
// Rule: anonymous visitors follow the source request's Accept-Language.
// A logged-in user's stored choice always wins.

const SUPPORTED_LOCALES = [
  'en', 'zh', 'es', 'fr', 'it',
  'ru', 'pt', 'ar', 'hi', 'ms', 'id', 'ja', 'ko', 'bn', 'ur', 'ta', 'vi',
  'my', 'th', 'el', 'tr', 'fa',
];

// Map regional Accept-Language tags to their supported base locale (or undefined).
// All 22 base locales above are now natively supported; only regional *variants*
// need aliasing. Languages without a dedicated UI translation are NOT down-mapped
// here — the frontend falls back to `en` gracefully for any untranslated root.
const LOCALE_ALIASES = {
  'zh-cn': 'zh',
  'zh-tw': 'zh',
  'zh-hk': 'zh',
  'en-gb': 'en',
  'en-us': 'en',
  'pt-br': 'pt',
  'pt-pt': 'pt',
  'es-mx': 'es',
  'es-419': 'es',
  'fr-ca': 'fr',
  'ja-jp': 'ja',
  'ko-kr': 'ko',
  'bn-bd': 'bn',
  'bn-in': 'bn',
  'ur-pk': 'ur',
  'hi-in': 'hi',
  'ar-sa': 'ar',
  'fa-ir': 'fa',
  'el-gr': 'el',
  'tr-tr': 'tr',
  'ms-bn': 'ms',
  'ms-my': 'ms',
  'id-id': 'id',
  'vi-vn': 'vi',
  'th-th': 'th',
  'my-mm': 'my',
  'ta-in': 'ta',
  'ta-lk': 'ta',
};

function normalizeLocale(raw) {
  if (!raw) return 'en';
  const lng = String(raw).toLowerCase().split('-')[0];
  if (SUPPORTED_LOCALES.includes(lng)) return lng;
  const alias = LOCALE_ALIASES[String(raw).toLowerCase()];
  if (alias && SUPPORTED_LOCALES.includes(alias)) return alias;
  // Unknown -> return the raw base so callers can detect "unsupported"
  // (rather than silently masquerading as 'en').
  return lng;
}

function parseAcceptLanguage(header) {
  if (!header) return [];
  return String(header)
    .split(',')
    .map((part) => {
      const [tag, ...params] = part.trim().split(';');
      let q = 1;
      for (const p of params) {
        const m = p.trim().match(/^q=([0-9.]+)$/i);
        if (m) q = parseFloat(m[1]);
      }
      return { tag: tag.trim().toLowerCase(), q: Number.isNaN(q) ? 1 : q };
    })
    .filter((x) => x.tag)
    .sort((a, b) => b.q - a.q);
}

function matchSupportedLocale(acceptLanguages, supported = SUPPORTED_LOCALES) {
  for (const { tag } of acceptLanguages) {
    const base = tag.split('-')[0];
    if (supported.includes(base)) return base;
    const alias = LOCALE_ALIASES[tag];
    if (alias && supported.includes(alias)) return alias;
  }
  return null;
}

// A locale code is "well-formed" if it looks like `xx` or `xx-YY`. We accept any
// well-formed code as a candidate for a logged-in user's stored choice, because
// the frontend gracefully falls back to `en` for anything it lacks a root file
// for. This keeps locale support GENERIC — adding a <lng>.json lights it up on
// the frontend with no backend list edit.
function isValidLocaleCode(raw) {
  return /^[a-z]{2}(-[A-Za-z]{2})?$/i.test(String(raw || '').trim());
}

// Anonymous visitors follow the source request's Accept-Language.
// A logged-in user's stored choice always wins (any well-formed code).
function resolveRequestLocale({
  acceptLanguage,
  userLocale,
  supported = SUPPORTED_LOCALES,
} = {}) {
  // A logged-in user's stored choice wins — any well-formed code is accepted
  // (the frontend falls back to `en` for untranslated roots). Malformed values
  // (e.g. 'xx', '') are ignored and we fall through to request detection.
  if (userLocale && isValidLocaleCode(userLocale)) {
    return String(userLocale).toLowerCase().split('-')[0];
  }
  const matched = matchSupportedLocale(parseAcceptLanguage(acceptLanguage), supported);
  return matched || 'en';
}

module.exports = {
  SUPPORTED_LOCALES,
  normalizeLocale,
  parseAcceptLanguage,
  matchSupportedLocale,
  isValidLocaleCode,
  resolveRequestLocale,
};
